import React, { useState, useEffect } from 'react';
import AddressAutocomplete from './AddressAutocomplete';

// US States list with abbreviations
const US_STATES = [
  { name: 'Alabama', abbr: 'AL' },
  { name: 'Alaska', abbr: 'AK' },
  { name: 'Arizona', abbr: 'AZ' },
  { name: 'Arkansas', abbr: 'AR' },
  { name: 'California', abbr: 'CA' },
  { name: 'Colorado', abbr: 'CO' },
  { name: 'Connecticut', abbr: 'CT' },
  { name: 'Delaware', abbr: 'DE' },
  { name: 'District of Columbia', abbr: 'DC' },
  { name: 'Florida', abbr: 'FL' },
  { name: 'Georgia', abbr: 'GA' },
  { name: 'Hawaii', abbr: 'HI' },
  { name: 'Idaho', abbr: 'ID' },
  { name: 'Illinois', abbr: 'IL' },
  { name: 'Indiana', abbr: 'IN' },
  { name: 'Iowa', abbr: 'IA' },
  { name: 'Kansas', abbr: 'KS' },
  { name: 'Kentucky', abbr: 'KY' },
  { name: 'Louisiana', abbr: 'LA' },
  { name: 'Maine', abbr: 'ME' },
  { name: 'Maryland', abbr: 'MD' },
  { name: 'Massachusetts', abbr: 'MA' },
  { name: 'Michigan', abbr: 'MI' },
  { name: 'Minnesota', abbr: 'MN' },
  { name: 'Mississippi', abbr: 'MS' },
  { name: 'Missouri', abbr: 'MO' },
  { name: 'Montana', abbr: 'MT' },
  { name: 'Nebraska', abbr: 'NE' },
  { name: 'Nevada', abbr: 'NV' },
  { name: 'New Hampshire', abbr: 'NH' },
  { name: 'New Jersey', abbr: 'NJ' },
  { name: 'New Mexico', abbr: 'NM' },
  { name: 'New York', abbr: 'NY' },
  { name: 'North Carolina', abbr: 'NC' },
  { name: 'North Dakota', abbr: 'ND' },
  { name: 'Ohio', abbr: 'OH' },
  { name: 'Oklahoma', abbr: 'OK' },
  { name: 'Oregon', abbr: 'OR' },
  { name: 'Pennsylvania', abbr: 'PA' },
  { name: 'Rhode Island', abbr: 'RI' },
  { name: 'South Carolina', abbr: 'SC' },
  { name: 'South Dakota', abbr: 'SD' },
  { name: 'Tennessee', abbr: 'TN' },
  { name: 'Texas', abbr: 'TX' },
  { name: 'Utah', abbr: 'UT' },
  { name: 'Vermont', abbr: 'VT' },
  { name: 'Virginia', abbr: 'VA' },
  { name: 'Washington', abbr: 'WA' },
  { name: 'West Virginia', abbr: 'WV' },
  { name: 'Wisconsin', abbr: 'WI' },
  { name: 'Wyoming', abbr: 'WY' }
];

// Custom State Autocomplete Component
const StateAutocomplete = ({ value, onChange, disabled, placeholder }) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredStates, setFilteredStates] = useState(US_STATES);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    
    // Filter states based on input
    const filtered = US_STATES.filter(state => 
      state.name.toLowerCase().includes(newValue.toLowerCase()) ||
      state.abbr.toLowerCase().includes(newValue.toLowerCase())
    );
    setFilteredStates(filtered);
    setShowDropdown(true);
    
    // Update parent component
    onChange(newValue);
  };

  const handleStateSelect = (state) => {
    setInputValue(state.abbr);
    onChange(state.abbr);
    setShowDropdown(false);
  };

  const handleFocus = () => {
    setShowDropdown(true);
    setFilteredStates(US_STATES);
  };

  const handleBlur = () => {
    // Delay hiding to allow click on dropdown items
    setTimeout(() => setShowDropdown(false), 150);
  };

  return (
    <div className="position-relative">
      <input
        type="text"
        className="form-control"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
      />
      {showDropdown && !disabled && filteredStates.length > 0 && (
        <div 
          className="dropdown-menu show position-absolute w-100" 
          style={{ 
            maxHeight: '200px', 
            overflowY: 'auto', 
            zIndex: 1000,
            top: '100%'
          }}
        >
          {filteredStates.slice(0, 10).map((state) => (
            <button
              key={state.abbr}
              className="dropdown-item"
              type="button"
              onMouseDown={() => handleStateSelect(state)}
            >
              {state.name} ({state.abbr})
            </button>
          ))}
        </div>
      )}
    </div>
  );
};


const AddressFields = ({ addressData, setAddressData, disabled }) => {
  // Set country to US by default
  useEffect(() => {
    if (!addressData.country) {
      setAddressData(prev => ({
        ...prev,
        country: 'US'
      }));
    }
  }, [addressData.country, setAddressData]);

  const handleChange = (e) => {
    setAddressData({
      ...addressData,
      [e.target.name]: e.target.value,
    });
  };

  const handleAddressSelect = (autocompleteData) => {
    setAddressData({
      ...addressData,
      ...autocompleteData
    });
  };


  const handleStateChange = (stateValue) => {
    setAddressData({
      ...addressData,
      state: stateValue
    });
  };

  return (
    <>
      <div className="mb-3">
        <label htmlFor="address_autocomplete" className="form-label">Address Search</label>
        <AddressAutocomplete
          onAddressSelect={handleAddressSelect}
          placeholder="Start typing your address..."
          disabled={disabled}
        />
      </div>
      <div className="mb-3">
        <label htmlFor="address_line_1" className="form-label">Address Line 1 *</label>
        <input
          type="text"
          className="form-control"
          id="address_line_1"
          name="address_line_1"
          value={addressData.address_line_1 || ''}
          onChange={handleChange}
          disabled={disabled}
          required
        />
      </div>
      <div className="mb-3">
        <label htmlFor="address_line_2" className="form-label">Address Line 2 (Optional)</label>
        <input
          type="text"
          className="form-control"
          id="address_line_2"
          name="address_line_2"
          value={addressData.address_line_2 || ''}
          onChange={handleChange}
          disabled={disabled}
        />
      </div>
      <div className="row mb-3">
        <div className="col-md-6">
          <label htmlFor="city" className="form-label">City</label>
          <input
            type="text"
            className="form-control"
            id="city"
            name="city"
            value={addressData.city || ''}
            onChange={handleChange}
            disabled={disabled}
            required
          />
        </div>
        <div className="col-md-3">
          <label htmlFor="state" className="form-label">State</label>
          <StateAutocomplete
            value={addressData.state || ''}
            onChange={handleStateChange}
            placeholder=""
            disabled={disabled}
          />
        </div>
        <div className="col-md-3">
          <label htmlFor="zip_code" className="form-label">ZIP Code</label>
          <input
            type="text"
            className="form-control"
            id="zip_code"
            name="zip_code"
            value={addressData.zip_code || ''}
            onChange={handleChange}
            disabled={disabled}
            required
          />
        </div>
      </div>
      <div className="mb-3">
        <label htmlFor="country" className="form-label">Country</label>
        <input
          type="text"
          className="form-control"
          id="country"
          name="country"
          value="United States"
          disabled={true}
          readOnly
        />
      </div>
    </>
  );
};

export default AddressFields;